import React, { useMemo, useState } from 'react';

import { RMDCalculationResponse } from '@ndvr/portfolio-api';

import { Page, PageContent, PageTitle, PageTitleRow, PageTopBar } from '@/components/ui/page-layout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageSpinner } from 'widgets';

import { useRmdByTenants } from './rmd-queries';
import { getRmdStatus, groupResultsByOwner, RmdOwnerGroup } from './rmd-utils';
import { RmdOwnerRow } from './RmdOwnerRow';

type StatusFilter = 'all' | 'at-risk' | 'on-track' | 'complete';

function matchesFilter(owner: RmdOwnerGroup, filter: StatusFilter): boolean {
    if (filter === 'all') return true;
    return getRmdStatus(owner.totalRmdObligation, owner.totalYtdSatisfied, owner.totalPlannedWithdrawals) === filter;
}

const RmdTrackerTitle: React.FC<{ taxYear: number }> = ({ taxYear }) => (
    <div>
        <PageTitle>RMD Tracker</PageTitle>
        <p className="text-cocoa-600 text-sm">Required Minimum Distributions · {taxYear} Tax Year</p>
    </div>
);

interface RmdTrackerTableProps {
    taxYear: number;
    response: RMDCalculationResponse;
}

const RmdTrackerTable: React.FC<RmdTrackerTableProps> = ({ taxYear, response }) => {
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

    const owners = useMemo(() => groupResultsByOwner(response.results), [response.results]);
    const filtered = useMemo(() => owners.filter((o) => matchesFilter(o, statusFilter)), [owners, statusFilter]);

    return (
        <Page>
            <PageTopBar />
            <PageTitleRow
                actions={
                    <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                        <SelectTrigger className="w-52">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Clients ({owners.length})</SelectItem>
                            <SelectItem value="on-track">On Track</SelectItem>
                            <SelectItem value="at-risk">At Risk</SelectItem>
                            <SelectItem value="complete">Complete</SelectItem>
                        </SelectContent>
                    </Select>
                }
                className="gap-4"
            >
                <RmdTrackerTitle taxYear={taxYear} />
            </PageTitleRow>

            <PageContent>
                <div className="rounded-md border bg-white">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-gray-50">
                                <TableHead className="w-8" />
                                <TableHead className="text-xs font-medium text-gray-500">Account Owner</TableHead>
                                <TableHead className="text-right text-xs font-medium text-gray-500">Age</TableHead>
                                <TableHead className="text-right text-xs font-medium text-gray-500"># Accts</TableHead>
                                <TableHead className="text-right text-xs font-medium text-gray-500">
                                    Prior Yr Balance
                                </TableHead>
                                <TableHead className="text-right text-xs font-medium text-gray-500">
                                    RMD Amount
                                </TableHead>
                                <TableHead className="text-right text-xs font-medium text-gray-500">
                                    Gross Dist. YTD
                                </TableHead>
                                <TableHead className="text-right text-xs font-medium text-gray-500">
                                    Planned Gross Dist.
                                </TableHead>
                                <TableHead className="min-w-[200px] text-xs font-medium text-gray-500">
                                    Progress
                                </TableHead>
                                <TableHead className="text-xs font-medium text-gray-500">Status</TableHead>
                                <TableHead className="w-10" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={11} className="py-12 text-center text-sm text-gray-400">
                                        No clients match the selected filter.
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((owner) => (
                                    <RmdOwnerRow key={owner.ownerPersonOrgGUID} owner={owner} taxYear={taxYear} />
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </PageContent>
        </Page>
    );
};

export const RmdTrackerView: React.FC = () => {
    const taxYear = new Date().getFullYear();
    const { data, isLoading, error } = useRmdByTenants();

    if (isLoading) {
        return (
            <Page>
                <PageTopBar />
                <PageTitleRow>
                    <RmdTrackerTitle taxYear={taxYear} />
                </PageTitleRow>
                <PageSpinner />
            </Page>
        );
    }

    if (error || !data) {
        return (
            <Page>
                <PageTopBar />
                <PageTitleRow>
                    <RmdTrackerTitle taxYear={taxYear} />
                </PageTitleRow>
                <PageContent>
                    <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                        Failed to load RMD data: {String(error)}
                    </div>
                </PageContent>
            </Page>
        );
    }

    return <RmdTrackerTable taxYear={taxYear} response={data} />;
};
